import { useQuery } from '@tanstack/react-query';
import {
  getDataEntries,
  getDataEntryById,
  getQuoteDocument,
  getDraftBlDocument,
  getRolloverDocument,
  getCopyDocuments,
  getCopyDocumentsByParams,
  getContainerImage,
  getContainerImageByParams,
} from '../services/dataEntryService';
import { queryKeys } from '../constants/queryKeys';

/**
 * Shared options for document GET queries.
 * - staleTime: Infinity    → fetched once; never considered stale automatically
 * - refetchOnWindowFocus: false → no re-fetch when switching browser tabs
 * - refetchOnMount: false  → no re-fetch when component re-mounts (e.g. tab change)
 *
 * Data is refreshed only when a mutation explicitly calls invalidateQueries().
 */
/**
 * docQueryOptions — for PDF/document queries that rarely change.
 * Fetched once; only refreshed when a mutation explicitly calls invalidateQueries().
 */
const docQueryOptions = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};

/**
 * imageQueryOptions — for container image queries.
 * staleTime: 0     → data is considered stale immediately after fetch
 * refetchOnMount: true → re-fetches when the component mounts / re-mounts
 * This ensures invalidateQueries() after an upload triggers a real API call.
 */
const imageQueryOptions = {
  staleTime: 0,
  refetchOnWindowFocus: false,
  refetchOnMount: true,
};

/**
 * useDataEntries — Fetch paginated data entry list
 * URL: GET /api/data-entry?page=1&search=...
 */
export const useDataEntries = (page = 1, search = '') => {
  return useQuery({
    queryKey: queryKeys.dataEntry.list(page, search),
    queryFn: () =>
      getDataEntries({
        page,
        pageSize: 20,
        search: search || undefined,
      }),
  });
};

/**
 * useDataEntry — Fetch a single data entry by ID (for edit mode)
 * URL: GET /api/data-entry/{id}
 */
export const useDataEntry = (id) => {
  return useQuery({
    queryKey: queryKeys.dataEntry.detail(id),
    queryFn: () => getDataEntryById(id),
    enabled: !!id && id !== 'new',
  });
};

/**
 * useQuoteDocument — Fetch quote document by fileName & blNo
 * URL: GET /api/data-entry/quote-document?fileName=&blNo=
 */
export const useQuoteDocument = (fileName, blNo) => {
  return useQuery({
    queryKey: ['dataEntry', 'quoteDoc', fileName, blNo],
    queryFn: () => getQuoteDocument(fileName, blNo),
    enabled: !!fileName && !!blNo,
    ...imageQueryOptions,
  });
};

/**
 * useDraftBlDocument — Fetch draft BL document by fileName & blNo
 * URL: GET /api/data-entry/draft-bl-document?fileName=&blNo=
 */
export const useDraftBlDocument = (fileName, blNo) => {
  return useQuery({
    queryKey: ['dataEntry', 'draftBlDoc', fileName, blNo],
    queryFn: () => getDraftBlDocument(fileName, blNo),
    enabled: !!fileName && !!blNo,
    ...imageQueryOptions,
  });
};

/**
 * useRolloverDocument — Fetch rollover document for a data entry
 * URL: GET /api/data-entry/{id}/rollover-document
 */
export const useRolloverDocument = (id) => {
  return useQuery({
    queryKey: queryKeys.dataEntry.rolloverDoc(id),
    queryFn: () => getRolloverDocument(id),
    enabled: !!id && id !== 'new',
    ...docQueryOptions,
  });
};

/**
 * useCopyDocuments — Fetch copy documents by query params (fileName, blNo)
 * URL: GET /api/data-entry/copy-documents?fileName=&blNo=
 */
export const useCopyDocuments = (fileName, blNo) => {
  return useQuery({
    queryKey: ['dataEntry', 'copyDocsParams', fileName, blNo],
    queryFn: () => getCopyDocuments(fileName, blNo),
    enabled: !!fileName,
    // Use imageQueryOptions (staleTime:0) so invalidateQueries after upload
    // triggers an immediate refetch — the derived docList then always has
    // real server IDs, eliminating the null-id delete race condition.
    ...imageQueryOptions,
  });
};

export const useCopyDocumentsByParams = useCopyDocuments;

/**
 * useContainerImage — Fetch container images by fileName, blNo & optional containerNo
 * URL: GET /api/data-entry/container-image?fileName=&blNo=&containerNo=
 */
export const useContainerImage = (fileName, blNo, containerNo) => {
  return useQuery({
    queryKey: ['dataEntry', 'containerImage', fileName, blNo, containerNo],
    queryFn: () => getContainerImage(fileName, blNo, containerNo),
    enabled: !!fileName,
    ...imageQueryOptions,
  });
};

/**
 * useContainerImageByParams — Fetch container image by query params (fileName, blNo, containerNo)
 * URL: GET /api/data-entry/container-image?fileName=&blNo=&containerNo=
 */
export const useContainerImageByParams = (fileName, blNo, containerNo) => {
  return useQuery({
    // Use the same 'containerImage' prefix as useContainerImage so that
    // invalidateQueries({ queryKey: ['dataEntry', 'containerImage'] })
    // invalidates BOTH hooks in a single call.
    queryKey: ['dataEntry', 'containerImage', fileName, blNo, containerNo],
    queryFn: () => getContainerImageByParams(fileName, blNo, containerNo),
    enabled: !!containerNo && !!fileName,
    ...imageQueryOptions,
  });
};

